import { Injectable } from "@angular/core";

import { StateService, WizardAnswers } from "./state.service";

import { WIZARD_STEPS } from "@zp-config/wizard-steps.config";
import { AdvicePointer } from "@zp-models/product";
import { Question } from "@zp-models/question";

@Injectable({
  providedIn: "root",
})
export class AdviceService {
  private readonly priorityMap: Record<AdvicePointer, number> = {
    Start: 1,
    Plus: 2,
    Pro: 3,
  };

  constructor(private stateService: StateService) {}
  /**
   * Geeft het hoogste advies op basis van een lijst AdvicePointers
   */
  getHighestAdvice(pointers: AdvicePointer[]): AdvicePointer {
    return pointers.reduce((highest, current) => {
      return this.priorityMap[current] > this.priorityMap[highest] ? current : highest;
    }, "Start");
  }

  /**
   * Bepaalt het eindadvies over alle stappen
   * Haalt de antwoorden nu van de StateService, wat de bron van waarheid is.
   */
  getFinalAdvice(): AdvicePointer {
    const currentAnswers: WizardAnswers = this.stateService.getAnswers();

    const pointers = Object.values(WIZARD_STEPS).map((step) =>
      this.getAdvicePointer(step, currentAnswers)
    );

    return this.getHighestAdvice(pointers);
  }

  /**
   * Bepaalt het adviespointer voor één stap
   * Let op: Deze functie krijgt de 'answers' nu als parameter mee.
   */
  getAdvicePointer(step: Question, answers: WizardAnswers): AdvicePointer {
    if (step.id === "step7") {
      return this.getStep7Advice();
    }

    const stepAnswer = answers[step.id];
    if (!stepAnswer) {
      console.warn(`Geen antwoord gevonden voor stap: ${step.id}, default naar 'Start'`);
      return "Start";
    }

    const pointers = this.getPointersForStep(step, stepAnswer);
    return pointers.length ? this.getHighestAdvice(pointers) : "Start";
  }

  /**
   * Speciale logica voor step7
   * Deze methode haalt de antwoorden al direct op via de stateService, dus dit is al correct.
   */
  private getStep7Advice(): AdvicePointer {
    const step2Answer = this.stateService.getAnswer("step2");
    const step7Answer = this.stateService.getAnswer("step7");

    // Als er geen antwoord is op step2 of budget_based is 'no'
    if (!step2Answer || step7Answer?.budget_based === "no") {
      return "Start";
    }

    // Haal de velden van step2 op
    const step2Fields = WIZARD_STEPS["step2"]?.fields?.[0];
    if (!step2Fields || !Array.isArray(step2Fields.options)) {
      console.warn("Step2 fields/options ontbreken");
      return "Start";
    }

    // Haal geselecteerde opties uit antwoord
    const selectedOptions = Array.isArray(step2Answer["mobility_options"])
      ? (step2Answer["mobility_options"] as string[])
      : [];

    // Zoek de pointers die bij de geselecteerde opties horen
    const pointers: AdvicePointer[] = selectedOptions
      .map(
        (optionValue) => step2Fields.options!.find((o) => o.value === optionValue)?.advicePointer
      )
      .filter((p): p is AdvicePointer => !!p); // Filter out undefined

    // Bepaal hoogste pointer
    const highestPointer = this.getHighestAdvice(pointers);

    return highestPointer === "Start" ? "Start" : "Plus";
  }

  /**
   * Haalt alle AdvicePointers op voor een stap en de gegeven antwoorden
   */
  private getPointersForStep(step: Question, stepAnswer: Record<string, unknown>): AdvicePointer[] {
    if (!step.fields?.length) return [];

    return step.fields.flatMap((field) => {
      const rawValue = stepAnswer[field.name];

      const selectedValues: string[] = Array.isArray(rawValue)
        ? rawValue
        : rawValue !== undefined
          ? [rawValue as string]
          : [];

      return selectedValues
        .map((value) => this.getAdvicePointerForOption(field, value))
        .filter((pointer): pointer is AdvicePointer => !!pointer);
    });
  }

  /**
   * Geeft de AdvicePointer terug voor een specifieke optie in een field
   */
  private getAdvicePointerForOption(
    field: Question["fields"][number],
    value: string
  ): AdvicePointer | undefined {
    const option = field.options?.find((opt) => opt.value === value);
    return option?.advicePointer;
  }
}
